/**
 * Resolving the far end of a beam from the pointer.
 *
 * Three priorities, in order: a cup or standard directly under the pointer
 * wins and the module is chosen from the distance; otherwise a standard lying
 * in the aimed direction at any module distance, nearest the pointer; failing
 * both, a free end at the selected length with the direction quantised to 15°.
 */

import { MM } from "../../data/haki.ts";
import { dist, nearestMod } from "../../model/geometry.ts";
import type { Leg, Point, ScaffoldModel } from "../../model/types.ts";

/** How far a distance may sit from a module and still count as that module, millimetres. */
const MODULE_TOLERANCE = 60;

/** Half-angle of the aiming cone, radians. */
const AIM_CONE = 0.3;

/** Direction quantisation for a free end. */
const FREE_END_STEP = Math.PI / 12;

export type AimResult = Readonly<{
  end: Point;
  target: Leg | null;
  y: number;
  len: number;
  /** False when the distance matches no module at all. */
  fits: boolean;
  /** True when the module differs from the length the user selected. */
  adjusted: boolean;
}>;

function moduleFor(from: Leg, to: Point): number | null {
  const d = dist(to, from) * 1000;
  const mod = nearestMod(d);

  return Math.abs(d - mod) <= MODULE_TOLERANCE ? mod : null;
}

function toLeg(from: Leg, leg: Leg, y: number, selectedSize: number): AimResult {
  const mod = moduleFor(from, leg);

  return {
    end: { x: leg.x, z: leg.z },
    target: leg,
    y,
    len: mod ?? nearestMod(dist(leg, from) * 1000),
    fits: mod !== null,
    adjusted: mod !== null && mod !== selectedSize,
  };
}

export type AimInput = Readonly<{
  model: ScaffoldModel;
  from: Leg;
  y: number;
  /** Where the pointer ray meets the horizontal plane at cup height. */
  planePoint: Point;
  /** A standard the pointer is directly over, if any. */
  hoveredLeg: Leg | null;
  selectedSize: number;
}>;

export function aimEnd(input: AimInput): AimResult {
  const { model, from, y, planePoint, hoveredLeg, selectedSize } = input;

  if (hoveredLeg && hoveredLeg !== from) {
    return toLeg(from, hoveredLeg, y, selectedSize);
  }

  const pointerAngle = Math.atan2(planePoint.x - from.x, planePoint.z - from.z);
  const pointerDistance = Math.hypot(planePoint.x - from.x, planePoint.z - from.z);
  let best: { leg: Leg; mod: number; score: number } | null = null;

  for (const leg of model.legs.values()) {
    if (leg === from) {
      continue;
    }

    const mod = moduleFor(from, leg);
    if (mod === null) {
      continue;
    }

    const angle = Math.atan2(leg.x - from.x, leg.z - from.z);
    const delta = Math.abs(
      ((((angle - pointerAngle + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI,
    );
    if (delta > AIM_CONE) {
      continue;
    }

    const score = Math.abs(dist(leg, from) - pointerDistance);
    if (!best || score < best.score) {
      best = { leg, mod, score };
    }
  }

  if (best && best.score < 1.2) {
    return {
      end: { x: best.leg.x, z: best.leg.z },
      target: best.leg,
      y,
      len: best.mod,
      fits: true,
      adjusted: best.mod !== selectedSize,
    };
  }

  const angle = Math.round(pointerAngle / FREE_END_STEP) * FREE_END_STEP;
  const reach = selectedSize * MM;

  return {
    end: {
      x: +(from.x + Math.sin(angle) * reach).toFixed(3),
      z: +(from.z + Math.cos(angle) * reach).toFixed(3),
    },
    target: null,
    y,
    len: selectedSize,
    fits: true,
    adjusted: false,
  };
}
